import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  Typography,
} from '@mui/material'
import React from 'react'
import { parseIntOr } from '../../../../../common/utils/parseNumericInput'
import {
  LIVE_FORCE_EXIT_TIMEFRAMES,
  LiveForceExitTimeframe,
  liveForceExitTime,
} from '../../../../../common/utils/forceExit'

const DEFAULT_RULE = { periodicity: 1, timeframe: 'minute' as LiveForceExitTimeframe }

/**
 * Close a position once it has been open this long, whatever the stop and target are
 * doing. Laid out like the backtest form's Trade Exit section so the same rule is
 * recognisable in both places.
 *
 * The pair is written as one object so a half-set rule can never be stored, and the
 * switch is read from the value rather than from its own state: a rule the engine would
 * refuse reads as off, which is what it is.
 */
function ForceExitField({ analyze, updateSetting }: { analyze: any; updateSetting: any }) {
  // The same predicate the normalizer and the engine use, so a stored rule they would
  // refuse — a legacy month carried in from a backtest, a zero duration — shows as off
  // rather than as the nearest live unit. Displaying "1 minute" over a stored month
  // would describe a close that never happens.
  const live = liveForceExitTime(analyze)

  const write = (rule?: { periodicity: number; timeframe: LiveForceExitTimeframe }) =>
    updateSetting('analyzeTrades.forceExit', rule ? { time: rule } : {})

  return (
    <>
      <Grid item xs={12} display="flex" alignItems="center">
        <Typography variant="body1">Force exit on time elapsed</Typography>
        <FormControl>
          <Switch
            checked={!!live}
            onChange={(e) => write(e.target.checked ? DEFAULT_RULE : undefined)}
            color="primary"
          />
        </FormControl>
      </Grid>

      {live && (
        <>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <InputLabel htmlFor="outlined-adornment-forceExitPeriodicity">
                Force exit periodicity
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-forceExitPeriodicity"
                name="analyzeTrades.forceExit.time.periodicity"
                type="number"
                value={live.periodicity}
                onChange={(e) => {
                  // Clearing the box to retype is a transient edit, not "no rule": writing
                  // the absence would unmount this field and flip the switch off mid-keystroke.
                  // The switch is how the rule is turned off. Same convention as
                  // RiskPerTradeField, which also has no unlimited state.
                  const parsed = parseIntOr(e.target.value, 0)
                  if (parsed <= 0) return
                  write({ periodicity: parsed, timeframe: live.timeframe })
                }}
                inputProps={{ min: 1 }}
                label="Force exit periodicity"
              />
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="force-exit-timeframe-label">Force exit timeframe</InputLabel>
              <Select
                labelId="force-exit-timeframe-label"
                value={live.timeframe}
                label="Force exit timeframe"
                onChange={(e) =>
                  write({
                    periodicity: live.periodicity,
                    timeframe: e.target.value as LiveForceExitTimeframe,
                  })
                }
              >
                {LIVE_FORCE_EXIT_TIMEFRAMES.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </>
      )}
    </>
  )
}

export default ForceExitField
